import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { IndicatorImportRef, IndicatorRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    AggregatedOutputColumn,
    IndicatorOutputColumn,
    SortModel,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { isNumber } from 'es-toolkit/compat';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import type { SlugMapDict } from 'src/lib/dictionary/dictionary';
import { escapeFormulaStringLiteral } from 'src/lib/escapeFormulaStringLiteral';
import { createTable } from 'src/lib/table';
import { AggregatedOutputStage, FilterStage, SourceStage } from 'src/lib/tableStages';
import { makeOptimizedTableSortKey } from 'src/models/TableModel';
import type { FullKeyType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';

export type AggregatedRiskTableKind = 'traders' | 'platforms' | 'symbols';

export interface AggregatedRiskTableFilters {
    platformIds: readonly string[];
    instrument: string | null;
}

export const EMPTY_AGGREGATED_RISK_TABLE_FILTERS = {
    platformIds: [],
    instrument: null,
} as const satisfies AggregatedRiskTableFilters;

type ServerTemplateColumn = Exclude<ColumnTemplate['columns'][number], { type: 'client' }>;
type IndicatorTemplateColumn = Extract<ColumnTemplate['columns'][number], { type: 'indicator' }>;

export interface AggregatedRiskTableQueryArgs {
    columnTemplate: ColumnTemplate;
    config: AggregatedRiskWidgetConfig;
    filters: AggregatedRiskTableFilters;
    signal: AbortSignal;
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    userViewKey: string;
}

interface AggregatedRiskTableQueryOptionsArgs {
    columnTemplate: ColumnTemplate;
    config: AggregatedRiskWidgetConfig;
    filters: AggregatedRiskTableFilters;
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    userViewKey: string;
}

function getRequiredIndicatorRef(slugMap: SlugMapDict, slug: keyof SlugMapDict): IndicatorRef {
    const indicator = slugMap[slug];
    if (!indicator) {
        throw new Error(`Missing required indicator for slug ${slug}`);
    }
    if (indicator.disabled) {
        throw new Error(`Required indicator for slug ${slug} is disabled`);
    }
    return { id: indicator.id };
}

function findGroupByTemplateColumn(
    columnTemplate: ColumnTemplate,
    indicator: IndicatorRef,
): IndicatorTemplateColumn | undefined {
    const matchingColumns = columnTemplate.columns.filter(
        (column): column is IndicatorTemplateColumn =>
            column.type === 'indicator' && column.indicator.id === indicator.id,
    );

    return matchingColumns.find((column) => column.visible !== false) ?? matchingColumns[0];
}

function makeGroupByColumn(
    columnTemplate: ColumnTemplate,
    slugMap: SlugMapDict,
    slug: keyof SlugMapDict,
): IndicatorOutputColumn {
    const indicator = getRequiredIndicatorRef(slugMap, slug);
    const templateColumn = findGroupByTemplateColumn(columnTemplate, indicator);
    return {
        type: 'indicator',
        id: templateColumn?.id ?? indicator.id,
        indicator,
    };
}

function isVisibleServerTemplateColumn(column: ColumnTemplate['columns'][number]): column is ServerTemplateColumn {
    return column.visible !== false && column.type !== 'client';
}

function hasExplicitSortDirection(column: ColumnTemplate['columns'][number]): boolean {
    return column.sort === 'asc' || column.sort === 'desc';
}

function getRequiredAggregationFunction(column: IndicatorTemplateColumn): IndicatorRef {
    if (!column.aggregationFunction) {
        throw new Error(`Missing aggregation function for aggregated indicator column ${column.id}`);
    }
    return column.aggregationFunction;
}

function makeAggregatedColumn(
    column: ColumnTemplate['columns'][number],
    groupBy: IndicatorOutputColumn,
): AggregatedOutputColumn | null {
    if (!isVisibleServerTemplateColumn(column)) {
        return null;
    }

    if (column.id === groupBy.id) {
        return null;
    }

    if (column.type === 'formula') {
        const aggregatedFormulaColumn = {
            type: 'formula' as const,
            id: column.id,
            formula: column.formula,
            imports: column.imports,
        };
        return column.options ? { ...aggregatedFormulaColumn, options: column.options } : aggregatedFormulaColumn;
    }

    if (column.indicator.id === groupBy.indicator.id) {
        return null;
    }

    const aggregatedIndicatorColumn = {
        type: 'indicator' as const,
        id: column.id,
        indicator: column.indicator,
        aggregationFunction: getRequiredAggregationFunction(column),
    };
    return column.options ? { ...aggregatedIndicatorColumn, options: column.options } : aggregatedIndicatorColumn;
}

function makeAggregatedColumns(
    columnTemplate: ColumnTemplate,
    groupBy: IndicatorOutputColumn,
): AggregatedOutputColumn[] {
    return columnTemplate.columns
        .map((column) => makeAggregatedColumn(column, groupBy))
        .filter((column): column is AggregatedOutputColumn => column !== null);
}

function makeColumnTemplateSort(columnTemplate: ColumnTemplate): SortModel | undefined {
    const sortColumns = columnTemplate.columns.filter(
        (column) =>
            isVisibleServerTemplateColumn(column) && isNumber(column.sortIndex) && hasExplicitSortDirection(column),
    );
    if (!sortColumns.length) {
        return undefined;
    }

    return sortColumns
        .toSorted((a, b) => a.sortIndex! - b.sortIndex!)
        .reduce(
            (sort, column) => ({
                ...sort,
                [column.id]: column.sort === 'desc' ? 'descending' : 'ascending',
            }),
            {},
        );
}

function makeVisibleServerTemplateColumnDataKey(column: ColumnTemplate['columns'][number]): string | null {
    if (!isVisibleServerTemplateColumn(column)) {
        return null;
    }

    if (column.type === 'formula') {
        return [
            'formula',
            column.formula,
            JSON.stringify(column.imports ?? []),
            JSON.stringify(column.options ?? null),
        ].join(':');
    }

    return [
        'indicator',
        column.indicator.id,
        column.aggregationFunction?.id ?? null,
        JSON.stringify(column.options ?? null),
    ].join(':');
}

function makeAggregatedRiskColumnTemplateQueryKey(columnTemplate: ColumnTemplate): string {
    return columnTemplate.columns
        .map((column): string | null => {
            const dataKey = makeVisibleServerTemplateColumnDataKey(column);
            if (!dataKey) {
                return null;
            }

            return [column.id, dataKey].join('::');
        })
        .filter((key): key is string => key !== null)
        .sort()
        .join('||');
}

function makeAggregatedRiskFiltersQueryKey(filters: AggregatedRiskTableFilters): string {
    return JSON.stringify({
        platformIds: filters.platformIds.toSorted(),
        instrument: filters.instrument,
    });
}

function makeFilterStage(
    sourceStage: SourceStage,
    filters: AggregatedRiskTableFilters,
    slugMap: SlugMapDict,
): SourceStage | FilterStage {
    const formulaParts: string[] = [];
    const imports: IndicatorImportRef[] = [];
    const platformIds = filters.platformIds.toSorted();

    if (platformIds.length > 0) {
        const alias = 'PLATFORM';
        imports.push({
            id: getRequiredIndicatorRef(slugMap, Slugs.accountPlatform).id,
            alias,
        });
        formulaParts.push(
            `(${platformIds.map((platformId) => `${alias} == '${escapeFormulaStringLiteral(platformId)}'`).join(' OR ')})`,
        );
    }

    if (filters.instrument !== null) {
        const alias = 'SYMBOL';
        imports.push({
            id: getRequiredIndicatorRef(slugMap, Slugs.positionSymbol).id,
            alias,
        });
        formulaParts.push(`${alias} == '${escapeFormulaStringLiteral(filters.instrument)}'`);
    }

    if (formulaParts.length === 0) {
        return sourceStage;
    }

    return FilterStage.fromInputStage(sourceStage, {
        type: 'filter',
        formula: formulaParts.join(' AND '),
        imports,
    });
}

async function createAggregatedTable(
    groupBy: IndicatorOutputColumn,
    columns: AggregatedOutputColumn[],
    columnTemplate: ColumnTemplate,
    inputStage: SourceStage | FilterStage,
    userViewKey: string,
    signal: AbortSignal,
): Promise<FullKeyType> {
    if (signal.aborted) {
        return null;
    }

    const sort = makeColumnTemplateSort(columnTemplate);
    const result = await createTable(
        AggregatedOutputStage.fromInputStage(inputStage, {
            groupBy,
            columns,
            sort,
        }).intoTable(userViewKey),
        {
            signal,
        },
    );

    if (signal.aborted) {
        return null;
    }

    return {
        ...result,
        fetchMode: sort ? 'snapshot' : 'diff',
    };
}

export function getAggregatedRiskTableQueryOptions(
    args: AggregatedRiskTableQueryOptionsArgs,
): QueryObserverOptions<FullKeyType, unknown, FullKeyType> {
    return {
        queryKey: [
            'aggregated-risk-widget-table',
            args.config.tableKind,
            args.config.sourceKey,
            args.userViewKey,
            makeAggregatedRiskFiltersQueryKey(args.filters),
            makeAggregatedRiskColumnTemplateQueryKey(args.columnTemplate),
            makeOptimizedTableSortKey(makeColumnTemplateSort(args.columnTemplate)),
        ],
        queryFn: ({ signal }) =>
            aggregatedRiskTableQueryFn({
                columnTemplate: args.columnTemplate,
                config: args.config,
                filters: args.filters,
                signal,
                slugMapServiceProvider: args.slugMapServiceProvider,
                userViewKey: args.userViewKey,
            }),
        staleTime: 0,
        gcTime: 0,
    };
}

export async function aggregatedRiskTableQueryFn({
    columnTemplate,
    config,
    filters,
    signal,
    slugMapServiceProvider,
    userViewKey,
}: AggregatedRiskTableQueryArgs): Promise<FullKeyType> {
    if (signal.aborted) {
        return null;
    }

    const slugMapService = await slugMapServiceProvider();
    if (signal.aborted) {
        return null;
    }

    const groupBy = makeGroupByColumn(columnTemplate, slugMapService.slugMap.value, config.groupBySlug);
    const columns = makeAggregatedColumns(columnTemplate, groupBy);
    const inputStage = makeFilterStage(SourceStage.allForKey(config.sourceKey), filters, slugMapService.slugMap.value);

    return createAggregatedTable(groupBy, columns, columnTemplate, inputStage, userViewKey, signal);
}
